import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSessionToken } from "../../hooks/useSession.js";
import { Breadcrumbs, Button, Card, CardDescription, CardTitle, LoadingStatus, Skeleton, StatusBadge } from "../ui/index.js";
import { PortalPageIntro, PortalWatermarkIcon } from "./PortalVisuals.js";
import { SetupStepHeader } from "./SetupStepHeader.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";
const RETURN_SYNC_ATTEMPTS = 12;
const RETURN_SYNC_INTERVAL_MS = 2000;

type Product = "DIALER_INTELELEAD" | "INSURANCE_TOOLKIT";

const details: Record<Product, { title: string; price?: string; steps?: string[] }> = {
  DIALER_INTELELEAD: {
    title: "Dialer Fee",
    price: "$26",
    steps: [
      "Complete secure checkout",
      "We finish your Netrio / Intelelead provisioning",
      "Move on to your training videos",
    ],
  },
  INSURANCE_TOOLKIT: {
    title: "Insurance Toolkit",
  },
};

interface Enrollment {
  product: Product;
  status: string;
  failedAt: string | null;
  failureMessage: string | null;
  hasPaidOrder: boolean;
  softPhone: { callDeliveryNumber: string; username: string } | null;
}

function enrollmentFromPayload(data: unknown, product: Product): Enrollment | null {
  if (!Array.isArray(data)) return null;
  return (data as Enrollment[]).find((entry) => entry.product === product) ?? null;
}

export interface PortalServicePageProps {
  product: Product;
  /**
   * "page" is the standalone service route (also where Square returns to);
   * "step" is the same content rendered as step 2 inside the Setup page.
   */
  variant?: "page" | "step";
  complete?: boolean;
  /** In "step" mode, moves the Setup page on to the training videos. */
  onStepComplete?: () => void;
}

export function PortalServicePage({ product, variant = "page", complete, onStepComplete }: PortalServicePageProps) {
  const token = getSessionToken();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnOrderId = searchParams.get("order")?.trim() || null;
  const [enrollment, setEnrollment] = useState<Enrollment | null | undefined>(undefined);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(Boolean(returnOrderId));
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const service = details[product];

  useEffect(() => {
    setError(null);
    setStatusMessage(null);
    if (!token) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    async function loadEnrollments(): Promise<Enrollment | null> {
      const response = await fetch(`${API_BASE}/api/services/enrollments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      if (!response.ok || !json.success) throw new Error(json.error || "Unable to load your setup");
      return enrollmentFromPayload(json.data, product);
    }

    async function syncReturnOrder(orderId: string): Promise<Enrollment | null> {
      const response = await fetch(`${API_BASE}/api/services/orders/${encodeURIComponent(orderId)}/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      if (response.status === 404) {
        throw new Error("We could not find that checkout session");
      }
      if (!response.ok || !json.success) {
        // Fall back to enrollments so webhook-confirmed payments still render.
        return loadEnrollments();
      }
      return (json.data?.enrollment as Enrollment | null | undefined) ?? null;
    }

    // Keep polling until the Intelelead invite task also succeeds, not just
    // until the softphone lands — payment can confirm well before either
    // finishes, and READY is only reached once both are done.
    function dialerAwaitingCompletion(latest: Enrollment | null) {
      return product === "DIALER_INTELELEAD"
        && Boolean(latest?.hasPaidOrder)
        && latest?.status !== "READY";
    }

    async function refreshAfterReturn(orderId: string) {
      setConfirmingPayment(true);
      let latest: Enrollment | null = null;
      for (let attempt = 0; attempt < RETURN_SYNC_ATTEMPTS; attempt += 1) {
        if (cancelled) return;
        try {
          // Re-query Square each attempt. Webhooks can lag/fail in sandbox; the
          // first sync can also race Square's eventually-consistent order state.
          latest = await syncReturnOrder(orderId);
          if (!cancelled) setEnrollment(latest);
          if (latest?.hasPaidOrder) {
            setSearchParams((current) => {
              const next = new URLSearchParams(current);
              next.delete("order");
              return next;
            }, { replace: true });
            // Keep polling briefly until softphone + invite are both done —
            // payment can confirm before either finishes.
            if (!dialerAwaitingCompletion(latest)) break;
          }
        } catch (loadError) {
          if (!cancelled) {
            setError(loadError instanceof Error ? loadError.message : "Unable to confirm your payment");
            setEnrollment(null);
          }
          break;
        }
        if (attempt < RETURN_SYNC_ATTEMPTS - 1) {
          await new Promise<void>((resolve) => {
            pollTimer = setTimeout(resolve, RETURN_SYNC_INTERVAL_MS);
          });
        }
      }
      if (!cancelled) {
        setConfirmingPayment(false);
        if (latest && !latest.hasPaidOrder) {
          setStatusMessage("Payment is still processing. Refresh this page in a moment, or continue to checkout if you still need to pay.");
        }
      }
    }

    async function refreshPaidDialerCredentials() {
      let latest: Enrollment | null = null;
      for (let attempt = 0; attempt < RETURN_SYNC_ATTEMPTS; attempt += 1) {
        if (cancelled) return;
        try {
          latest = await loadEnrollments();
          if (!cancelled) setEnrollment(latest);
          if (!dialerAwaitingCompletion(latest)) return;
        } catch (loadError) {
          if (!cancelled) {
            setError(loadError instanceof Error ? loadError.message : "Unable to load your setup");
            setEnrollment(null);
          }
          return;
        }
        if (attempt < RETURN_SYNC_ATTEMPTS - 1) {
          await new Promise<void>((resolve) => {
            pollTimer = setTimeout(resolve, RETURN_SYNC_INTERVAL_MS);
          });
        }
      }
    }

    if (returnOrderId) {
      void refreshAfterReturn(returnOrderId);
      return () => {
        cancelled = true;
        if (pollTimer) clearTimeout(pollTimer);
      };
    }

    setConfirmingPayment(false);
    void loadEnrollments()
      .then((latest) => {
        if (cancelled) return;
        setEnrollment(latest);
        if (dialerAwaitingCompletion(latest)) {
          void refreshPaidDialerCredentials();
        }
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load your setup");
        setEnrollment(null);
      });

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [product, token, returnOrderId, setSearchParams]);

  async function beginCheckout() {
    if (!token) return;
    setStartingCheckout(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/services/${product}/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      if (!response.ok || !json.success) throw new Error(json.error || "Unable to start checkout");
      window.location.assign(json.data.checkoutUrl);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : "Unable to start checkout");
      setStartingCheckout(false);
    }
  }

  const breadcrumbs = (
    <Breadcrumbs
      items={[
        { label: "Setup", to: "/my-agent" },
        { label: service.title, current: true },
      ]}
    />
  );

  if (enrollment === undefined) {
    return (
      <section role="status" aria-busy="true" className="space-y-8">
        <LoadingStatus label={confirmingPayment ? `Confirming ${service.title} payment` : `Loading ${service.title} setup`} />
        <div className="space-y-3 border-b-2 border-border-default pb-7">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-12 w-80 max-w-full" />
        </div>
        <Skeleton className="h-[300px] w-full rounded-card" />
        <Skeleton className="h-52 w-full rounded-card" />
      </section>
    );
  }

  const hasPaidEntitlement = Boolean(enrollment?.hasPaidOrder);
  // Insurance Toolkit is no longer a separate purchase — it's auto-provisioned
  // as soon as the dialer fee is paid, so "unlocked" here means provisioning has
  // started, not that this product itself was paid for.
  const unlocked = product === "INSURANCE_TOOLKIT"
    ? Boolean(enrollment) && enrollment!.status !== "NOT_STARTED"
    : hasPaidEntitlement;
  const hasSoftPhone = Boolean(enrollment?.softPhone);
  const canCheckout = product === "DIALER_INTELELEAD"
    && !error
    && !confirmingPayment
    && !hasPaidEntitlement
    && (!enrollment || ["NOT_STARTED", "PENDING_PAYMENT", "FAILED", "REFUNDED"].includes(enrollment.status));
  const isReady = enrollment?.status === "READY";
  const statusBadge = enrollment
    ? (
      <StatusBadge
        status={
          enrollment.status === "READY" || hasSoftPhone
            ? "ACTIVE"
            : confirmingPayment
              ? "PENDING"
              : unlocked
                ? "PENDING"
                : enrollment.status === "FAILED"
                  ? "NIGO"
                  : "PENDING"
        }
        size="sm"
      />
    )
    : null;

  return (
    <section className="space-y-8" aria-labelledby={variant === "step" ? undefined : "service-title"}>
      <motion.header className="contents" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        {variant === "step" ? (
          <SetupStepHeader stepId="dialer" complete={complete} action={statusBadge} />
        ) : (
          <PortalPageIntro
            titleId="service-title"
            title={service.title}
            breadcrumbs={breadcrumbs}
            action={statusBadge}
          />
        )}
      </motion.header>

      {unlocked ? (
        <div className="space-y-3">
          <p
            role="status"
            className="rounded-xl border border-status-active-border bg-status-active-bg px-4 py-3 text-sm font-semibold text-status-active-text"
          >
            {product === "DIALER_INTELELEAD"
              ? hasSoftPhone
                ? isReady
                  ? "Payment successful. Your Netrio credentials are assigned — you'll pick them up in step 4."
                  : "Payment successful. Your Netrio credentials are assigned — the Intelelead email should arrive in your inbox soon."
                : "Payment successful. Start your training videos while we finish assigning your Netrio credentials."
              : isReady
                ? "Your Toolkit account is ready."
                : "We're setting up your Toolkit account now — this was kicked off automatically by your dialer fee payment."}
          </p>
          {product === "DIALER_INTELELEAD" && isReady && (
            <p
              role="status"
              className="rounded-xl border border-status-active-border bg-surface-raised px-4 py-3 text-sm font-semibold text-status-active-text"
            >
              Your Intelelead invite has been sent — check your email to finish linking your account.
            </p>
          )}
          {product === "INSURANCE_TOOLKIT" && (
            <p
              role="status"
              className="rounded-xl border border-status-pending-border bg-status-pending-bg px-4 py-3 text-sm font-medium text-status-pending-text"
            >
              Look out for an email.
            </p>
          )}
          {error && <p role="alert" className="text-sm font-semibold text-status-rejected-text">{error}</p>}
        </div>
      ) : product === "INSURANCE_TOOLKIT" ? (
        <Card padding="lg" variant="elevated" className="relative min-h-[300px] overflow-hidden border-2 bg-status-nigo-bg border-status-nigo-border">
          <div className="pointer-events-none absolute -bottom-10 -right-8 h-[150px] w-[150px] text-status-nigo-text opacity-[0.12]" aria-hidden>
            <PortalWatermarkIcon kind="toolkit" />
          </div>
          <div className="relative z-10 flex min-h-[250px] flex-col gap-6">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-content-secondary">Included with the dialer fee</p>
              <p className="mt-3 max-w-xl text-2xl font-extrabold leading-snug text-content-primary">
                No separate payment needed — your Toolkit account is created automatically once you pay the dialer fee.
              </p>
            </div>
            <Button className="mt-auto w-full sm:max-w-md" size="lg" onClick={() => navigate("/my-agent?step=dialer")}>
              Go to the dialer fee
              <span aria-hidden="true">→</span>
            </Button>
            {error && <p role="alert" className="text-sm font-semibold text-status-rejected-text">{error}</p>}
          </div>
        </Card>
      ) : (
        <Card padding="lg" variant="elevated" className="relative min-h-[300px] overflow-hidden border-2 bg-status-active-bg border-status-active-border">
          <div className="pointer-events-none absolute -bottom-10 -right-8 h-[150px] w-[150px] text-status-active-text opacity-[0.12]" aria-hidden>
            <PortalWatermarkIcon kind="dialer" />
          </div>
          <div className="relative z-10 flex min-h-[250px] flex-col gap-6">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-content-secondary">One-time setup</p>
              <p className="mt-3 font-display text-6xl font-extrabold tracking-[-0.06em] text-content-primary">{service.price}</p>
              <p className="mt-2 max-w-xl text-sm font-medium text-content-secondary">Secure payment unlocks your account provisioning and access instructions.</p>
            </div>
            {canCheckout && (
              <Button className="mt-auto w-full sm:max-w-md" size="lg" disabled={startingCheckout} onClick={() => void beginCheckout()}>
                {startingCheckout ? "Opening secure checkout…" : `Continue to checkout · ${service.price}`}
                <span aria-hidden="true">→</span>
              </Button>
            )}
            {confirmingPayment && (
              <p role="status" className="rounded-xl border border-status-pending-border bg-surface-raised px-4 py-3 text-sm text-status-pending-text">
                Confirming your payment with Square…
              </p>
            )}
            {statusMessage && !confirmingPayment && (
              <p role="status" className="rounded-xl border border-status-pending-border bg-surface-raised px-4 py-3 text-sm text-status-pending-text">
                {statusMessage}
              </p>
            )}
            {error && <p role="alert" className="text-sm font-semibold text-status-rejected-text">{error}</p>}
          </div>
        </Card>
      )}

      {product === "DIALER_INTELELEAD" && !unlocked && (
        <Card padding="lg" className="border-2">
          <CardTitle className="text-2xl font-extrabold">What happens next</CardTitle>
          <CardDescription className="mt-2">Each step is handled automatically after Square confirms your payment.</CardDescription>
          <ol className="mt-6 grid gap-3 sm:grid-cols-3">
            {(service.steps ?? []).map((step, index) => {
              const stepActive = confirmingPayment ? index === 0 : index === 0;
              return (
                <li
                  key={step}
                  className={`rounded-card border-2 p-5 ${
                    stepActive
                      ? "border-accent-strong bg-surface-raised"
                      : "border-border-default bg-surface-overlay"
                  }`}
                >
                  <span className={`grid h-9 w-9 place-items-center rounded-lg text-sm font-extrabold ${
                    stepActive
                      ? "bg-accent-strong text-on-accent"
                      : "bg-surface-raised text-content-secondary"
                  }`}>{index + 1}</span>
                  <p className="mt-3 text-sm font-medium leading-5 text-content-primary">{step}</p>
                </li>
              );
            })}
          </ol>
        </Card>
      )}

      {product === "DIALER_INTELELEAD" && unlocked && (
        <Card padding="lg" className="border-2">
          <CardTitle className="text-2xl font-extrabold">Next: your training videos</CardTitle>
          <CardDescription className="mt-2">
            Step 3 walks you through Ring IQ in nine short videos — registering, funding your account, setting
            your call delivery number, and running your day. Step 4 then hands you your Netrio desktop app
            credentials.
          </CardDescription>
          <Button
            className="mt-6"
            size="lg"
            onClick={() => (onStepComplete ? onStepComplete() : navigate("/my-agent?step=training"))}
          >
            Start training
            <span aria-hidden="true">→</span>
          </Button>
        </Card>
      )}
    </section>
  );
}
